/**
 * HR-only read-receipts drawer for an announcement.
 *
 * Shows who's read it (with timestamp), who hasn't, and offers a
 * one-click "remind unread" which drops in-app notifications.
 */
import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { useToast } from "@/hooks/useToast";
import {
  getAnnouncementReadReceipts,
  remindUnreadAnnouncement,
  type ReadReceiptsSummary,
} from "@/api/announcements";

interface Props {
  isOpen: boolean;
  announcementId: string | null;
  onClose: () => void;
}

export function ReadReceiptsModal({ isOpen, announcementId, onClose }: Props) {
  const { addToast } = useToast();
  const [data, setData] = useState<ReadReceiptsSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [reminding, setReminding] = useState(false);

  useEffect(() => {
    if (!isOpen || !announcementId) return;
    setLoading(true);
    getAnnouncementReadReceipts(announcementId)
      .then(setData)
      .catch(() => addToast("error", "Failed to load read receipts"))
      .finally(() => setLoading(false));
  }, [isOpen, announcementId, addToast]);

  const remind = async () => {
    if (!announcementId) return;
    setReminding(true);
    try {
      const n = await remindUnreadAnnouncement(announcementId);
      addToast(
        "success",
        n === 0 ? "Nothing to remind — everyone's read it." : `Reminded ${n} ${n === 1 ? "person" : "people"}`
      );
    } catch {
      addToast("error", "Failed to send reminders");
    } finally {
      setReminding(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Read receipts" size="lg">
      {loading || !data ? (
        <p className="text-sm text-text-muted">Loading…</p>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <p className="text-3xl font-bold font-heading text-text-primary">
                {data.percent_read.toFixed(0)}%
              </p>
              <p className="text-xs text-text-muted">
                {data.total_read} of {data.total_eligible} eligible teammates
              </p>
            </div>
            {data.unread_user_ids.length > 0 && (
              <Button
                variant="secondary"
                size="sm"
                onClick={remind}
                disabled={reminding}
              >
                {reminding ? "Sending…" : `Remind ${data.unread_user_ids.length} unread`}
              </Button>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-text-primary mb-2">
              Read ({data.reads.length})
            </p>
            {data.reads.length === 0 ? (
              <p className="text-sm text-text-muted">No reads yet.</p>
            ) : (
              <ul className="divide-y divide-border rounded-lg border border-border max-h-80 overflow-y-auto">
                {data.reads.map((r) => (
                  <li
                    key={r.user_id}
                    className="flex items-center justify-between px-3 py-2 text-sm"
                  >
                    <div>
                      <p className="text-text-primary">{r.name}</p>
                      {r.email && (
                        <p className="text-xs text-text-muted">{r.email}</p>
                      )}
                    </div>
                    <Badge variant="default">
                      {new Date(r.read_at).toLocaleString()}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {data.unread_user_ids.length > 0 && (
            <p className="text-xs text-text-muted">
              {data.unread_user_ids.length}{" "}
              {data.unread_user_ids.length === 1 ? "person hasn't" : "people haven't"} read
              this yet.
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
